
import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Clock } from "lucide-react";
import { MedewerkerResponse } from "@/integrations/supabase/client";
import { UserRole } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MedewerkersTableProps {
  medewerkers: MedewerkerResponse[];
  onEdit: (medewerker: MedewerkerResponse) => void;
  onDelete: (id: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const MedewerkersTable = ({ 
  medewerkers, 
  onEdit, 
  onDelete, 
  canEdit = false,
  canDelete = false
}: MedewerkersTableProps) => {
  const { user } = useAuth();

  // Log permissions for debugging
  useEffect(() => {
    console.log("MedewerkersTable - Current permissions:", {
      canEdit,
      canDelete,
      userRole: user?.role
    });
  }, [canEdit, canDelete, user]);

  if (medewerkers.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-md">
        <p className="text-muted-foreground">Geen medewerkers gevonden</p>
      </div>
    );
  }

  // Functie om de rol te bepalen op basis van functie
  const bepaalRol = (medewerker: MedewerkerResponse): UserRole => {
    if (!medewerker.functie) return "medewerker";
    
    const functie = medewerker.functie.toLowerCase();
    if (functie.includes('management') || functie.includes('admin')) {
      return "management";
    } else if (functie.includes('planner')) {
      return "planner";
    }
    return "medewerker";
  };

  // Functie om de juiste badge kleur te bepalen op basis van rol
  const getRolBadgeVariant = (rol: UserRole): "default" | "outline" | "secondary" | "destructive" => {
    switch (rol) {
      case "management":
        return "destructive";
      case "planner":
        return "secondary";
      default:
        return "default";
    }
  };

  // Format the last_logout timestamp
  const formatLogoutTime = (timestamp: string | null) => {
    if (!timestamp) return "-";
    try {
      return format(new Date(timestamp), "d MMM yyyy HH:mm", { locale: nl });
    } catch (error) {
      console.error("Error formatting date:", error);
      return timestamp;
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Naam</TableHead>
          <TableHead>Gebruikersnaam</TableHead>
          <TableHead>Rol</TableHead>
          <TableHead>Functie</TableHead>
          <TableHead>Afdeling</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Laatste uitgelogd</TableHead>
          <TableHead className="text-right">Acties</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {medewerkers.map((medewerker) => {
          const rol = bepaalRol(medewerker);
          
          // Check if current user can edit/delete this specific medewerker
          // Management can edit/delete anyone, others can only manage medewerkers
          const canEditThis = canEdit && (user?.role === "management" || rol === "medewerker");
          const canDeleteThis = canDelete && (user?.role === "management" || rol === "medewerker");
          
          return (
            <TableRow key={medewerker.id}>
              <TableCell className="font-medium">{medewerker.naam}</TableCell>
              <TableCell>{medewerker.username || "-"}</TableCell>
              <TableCell>
                <Badge variant={getRolBadgeVariant(rol)} className="capitalize">
                  {rol}
                </Badge>
              </TableCell>
              <TableCell>{medewerker.functie || "-"}</TableCell>
              <TableCell>{medewerker.afdeling || "-"}</TableCell>
              <TableCell>{medewerker.email || "-"}</TableCell>
              <TableCell>
                {medewerker.last_logout ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>{formatLogoutTime(medewerker.last_logout)}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        Laatste keer uitgelogd
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-right space-x-2">
                {canEditThis && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onEdit(medewerker)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Wijzigen
                  </Button>
                )}
                {canDeleteThis && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => onDelete(medewerker.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Verwijderen
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default MedewerkersTable;
